import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabase';

export default function StudentDashboard() {
    const [user, setUser] = useState<any>(null);
    const [studentProfile, setStudentProfile] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) { navigate('/student-login'); return; }

            // Verify user has a student record in DB
            const { data: student } = await supabase.from('students').select('*').eq('user_id', session.user.id).maybeSingle();
            if (!student) { 
                await supabase.auth.signOut();
                localStorage.removeItem('userRole');
                navigate('/student-login'); 
                return; 
            }

            setUser(session.user);
            setStudentProfile(student);
            // Keep localStorage in sync for sidebar role display
            localStorage.setItem('userRole', 'student');
            localStorage.setItem('studentName', student.name);
            localStorage.setItem('studentGrade', student.grade || '');
            setLoading(false);
        };

        checkAuth();
    }, [navigate]);

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, border: '3px solid #e2e8f0', borderTopColor: '#197fe6', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 1rem' }} />
                <p style={{ color: '#64748b' }}>Loading...</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );
    if (!user) return null;

    const studentName = studentProfile?.name || user.user_metadata?.full_name || user.email;
    const studentGrade = studentProfile?.grade || 'Not set';

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #f8fbff 0%, #f8fafc 36%, #ffffff 100%)', padding: 'clamp(1.25rem, 4vw, 3rem) clamp(0.75rem, 3vw, 3rem)' }}>
            <div style={{ maxWidth: '960px', margin: '0 auto' }}>
                <div style={{ marginBottom: '2rem' }}>
                    <span style={{ display: 'inline-block', background: 'rgba(25,127,230,0.1)', color: '#197fe6', padding: '0.35rem 0.9rem', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.75rem' }}>Student Dashboard</span>
                    <h1 style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 800, color: '#0f172a', margin: 0 }}>Welcome, {studentName}!</h1>
                    <p style={{ margin: '0.65rem 0 0', color: '#64748b', fontSize: '0.96rem', lineHeight: 1.7, maxWidth: '40rem' }}>
                        Keep your learning journey organized, revisit the right teachers faster, and return to the platform with a cleaner mobile experience.
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem', background: 'rgba(255,255,255,0.94)', padding: 'clamp(1rem, 3vw, 1.5rem) clamp(1rem, 4vw, 2rem)', borderRadius: '24px', border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 20px 45px rgba(15,23,42,0.06)', flexWrap: 'wrap' }}>
                    {user.user_metadata?.avatar_url && (
                        <img src={user.user_metadata.avatar_url} alt={studentName} style={{ width: '80px', height: '80px', borderRadius: '16px', objectFit: 'cover', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    )}
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ margin: '0 0 0.25rem', color: '#64748b', fontSize: '0.85rem' }}>{user.email}</p>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ background: 'rgba(16,185,129,0.1)', color: '#059669', padding: '0.3rem 0.7rem', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 700 }}>{studentGrade}</span>
                            <span style={{ background: '#f1f5f9', color: '#64748b', padding: '0.3rem 0.7rem', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 600 }}>Student</span>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))', gap: '1.25rem' }}>
                    <div style={{ background: 'rgba(255,255,255,0.95)', padding: '1.75rem', borderRadius: '24px', border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 16px 40px rgba(15,23,42,0.05)' }}>
                        <h3 style={{ color: '#0f172a', marginTop: 0 }}>Browse Teachers</h3>
                        <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>Find the perfect tutor for your subjects</p>
                        <Link to="/find-teachers" className="btn primary">Find Teachers</Link>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.95)', padding: '1.75rem', borderRadius: '24px', border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 16px 40px rgba(15,23,42,0.05)' }}>
                        <h3 style={{ color: '#0f172a', marginTop: 0 }}>My Courses</h3>
                        <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>View your enrolled courses</p>
                        <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem', margin: 0 }}>Coming soon!</p>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.95)', padding: '1.75rem', borderRadius: '24px', border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 16px 40px rgba(15,23,42,0.05)' }}>
                        <h3 style={{ color: '#0f172a', marginTop: 0 }}>My Progress</h3>
                        <p style={{ color: '#64748b', marginBottom: '1rem', fontSize: '0.9rem' }}>Track your learning journey</p>
                        <p style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.85rem', margin: 0 }}>Coming soon!</p>
                    </div>
                </div>

                <div style={{ marginTop: '2rem', padding: 'clamp(1.25rem, 4vw, 1.75rem) clamp(1rem, 4vw, 2rem)', background: 'rgba(255,255,255,0.95)', borderRadius: '24px', border: '1px solid rgba(148,163,184,0.16)', boxShadow: '0 16px 40px rgba(15,23,42,0.05)' }}>
                    <h2 style={{ color: '#0f172a', marginTop: 0, fontSize: '1.15rem' }}>Quick Actions</h2>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                        <Link to="/find-teachers" className="btn secondary">Browse All Teachers</Link>
                        <Link to="/about" className="btn tertiary">About LankaTutor</Link>
                        <Link to="/contact" className="btn tertiary">Contact Support</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
